// Lib
import React, { useCallback, useMemo } from 'react';
import classNames from 'classnames';
import { IMAGE_TYPES } from '../../../../common/media/mediaConstants';
import ElementImage from '../../../components/images/ElementImage';
import { ImMNElement } from '../../../../common/elements/elementModelTypes';
import RichMediaPreviewOverlay from '../../richMedia/components/RichMediaPreviewOverlay';
import { getElementId } from '../../../../common/elements/utils/elementPropertyUtils';
import IPhoneVideoPlayerModal from './IPhoneVideoPlayerModal';

interface IphoneVideoPlayerPreviewProps {
    element: ImMNElement;
    mediaPlayerClasses: string;
    paddingTop: string;
    widthPx: number;
    showDownloadOption: boolean;
    showVideoPlayerModal: boolean;
    setShowVideoPlayerModal: (show: boolean) => void;
    showPreviewOverlay: boolean;
}

/**
 * This component is used to preview a video on iPhones, rather than showing the video player on the canvas.
 * This is because the video player will automatically create a new composition layer, which appears to be the cause
 * of a large number of crashes on iPhones.
 *
 * So we'll instead show a preview of the video, which will be a static image - and when the user clicks on it,
 * it will open the video within the modal.
 */
const IPhoneVideoPlayerPreview: React.FC<IphoneVideoPlayerPreviewProps> = (props) => {
    const {
        element,
        showDownloadOption,
        mediaPlayerClasses,
        paddingTop,
        widthPx,
        showVideoPlayerModal,
        setShowVideoPlayerModal,
        showPreviewOverlay,
    } = props;

    const elementId = getElementId(element);

    const style = useMemo(() => ({ paddingTop }), [paddingTop]);
    const forcedSize = useMemo(() => ({ width: widthPx }), [widthPx]);

    const handlePlayButtonClick = useCallback(
        (event) => {
            event.preventDefault();
            event.stopPropagation();
            setShowVideoPlayerModal(true);
        },
        [elementId],
    );

    return (
        <div className={classNames('IPhoneVideoPlayerPreview', mediaPlayerClasses)}>
            <div className="media-player-container" style={style}>
                <ElementImage
                    imageType={IMAGE_TYPES.FILE}
                    widthPx={widthPx}
                    forcedSize={forcedSize}
                    element={element}
                />
            </div>
            <RichMediaPreviewOverlay show={showPreviewOverlay} onClick={handlePlayButtonClick} />
            {showVideoPlayerModal && (
                <IPhoneVideoPlayerModal
                    element={element}
                    setShowVideoPlayerModal={setShowVideoPlayerModal}
                    showDownloadOption={showDownloadOption}
                />
            )}
        </div>
    );
};

export default IPhoneVideoPlayerPreview;
